import { useIsPublicChat } from '@/components/Chat/useIsPublicChat';
import { useChatEditorTiptap } from '@/components/ChatV2/ChatV2Input/hooks/useChatInputEditor';
import { convertAttachments } from '@/components/ChatV2/ChatV2Input/utils/attachments';
import { api } from '@/lib/trpc/react';
import type { HighlightRect } from '@anaralabs/lector';
import { skipToken } from '@tanstack/react-query';
import type { ChatbotType } from '@unriddle/db/types';
import type { AnaraAttachment } from '@unriddle/validators/message/attachments';
import { nanoid } from 'nanoid';
import type { JSONContent } from 'novel';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import { useSettings } from './useSettings';

export const mapTextToAttachment = (
  chatbotId: string,
  text: string,
  chatbotType: 'NOTE' | 'DOCUMENT',
  highlights?: HighlightRect[],
): AnaraAttachment => {
  return {
    id: nanoid(),
    type: 'input-attachment',
    data: {
      type: 'text',
      text,
      chatbotId,
      chatbotType,
      highlights,
    },
  };
};

export const mapMentionToAttachment = (
  mentionChatbotId: string,
  mentionChatbotName: string,
  mentionChatbotType: ChatbotType,
): AnaraAttachment => {
  return {
    id: nanoid(),
    type: 'input-attachment',
    data: {
      type: 'mention',
      chatbotId: mentionChatbotId,
      chatbotName: mentionChatbotName,
      chatbotType: mentionChatbotType,
    },
  } satisfies AnaraAttachment;
};

export const useSavedChatbotInput = (chatbotId?: string) => {
  const { isPublicPaper } = useIsPublicChat();
  const { data: latestInput, isLoading } =
    api.chatbotInput.getNewChatInput.useQuery(
      chatbotId ? { chatbotId } : skipToken,
      {
        enabled: !!chatbotId && !isPublicPaper,
        retry: 1,
        staleTime: Number.POSITIVE_INFINITY,
      },
    );

  const filteredAttachments = latestInput?.attachments?.length
    ? convertAttachments(latestInput.attachments)
    : [];

  return {
    data: latestInput,
    isLoading,
    input: latestInput?.input,
    attachments: filteredAttachments as Extract<
      AnaraAttachment,
      { type: 'input-attachment' }
    >[],
  };
};

export const useChatbotInput = () => {
  const utils = api.useUtils();
  const { settings } = useSettings();
  const { removeMention, editor } = useChatEditorTiptap();
  const { cleanupEditor } = useChatEditorTiptap();

  const { isPublicPaper } = useIsPublicChat();
  const { mutate } = api.chatbotInput.saveInputChatV2.useMutation({
    onMutate: async (newData) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await utils.chatbotInput.getNewChatInput.cancel();

      // Snapshot the previous value
      const previousData = utils.chatbotInput.getNewChatInput.getData();

      // Optimistically update to the new value
      utils.chatbotInput.getNewChatInput.setData(
        { chatbotId: newData.chatbotId },
        {
          attachments: newData.attachments as AnaraAttachment[],
          input: newData.input,
        },
      );

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_err, _newData, context) => {
      // If the mutation fails, use the context we returned above to roll back
      if (context?.previousData) {
        utils.chatbotInput.getNewChatInput.setData(
          { chatbotId: _newData.chatbotId },
          context.previousData,
        );
      }
    },
  });

  const addAttachments = (
    newAttachments: AnaraAttachment[],
    chatbotId: string,
  ) => {
    if (isPublicPaper) return;

    if (
      settings.modelName === 'deepseek-r1-distill-llama-70b' &&
      newAttachments.some((attachment) => attachment.data.type === 'image')
    ) {
      toast.error('Model does not support attachments');
      return;
    }

    const content = editor?.getJSON();

    const currentAttachments = convertAttachments(
      utils.chatbotInput.getNewChatInput.getData({
        chatbotId,
      })?.attachments ?? [],
    );
    const attachments: AnaraAttachment[] = [
      ...currentAttachments,
      ...newAttachments,
    ];

    if (isPublicPaper) return;

    mutate({ chatbotId, attachments, input: content ?? '' });
  };

  const removeAttachment = (id: string, chatbotId: string) => {
    if (isPublicPaper) return;

    const attachments = (utils.chatbotInput.getNewChatInput.getData({
      chatbotId,
    })?.attachments ?? []) as AnaraAttachment[];

    const filteredAttachments = attachments.filter(
      (attachment) => attachment.id !== id,
    );

    const deletedAttachment: AnaraAttachment | undefined = attachments.find(
      (attachment) => attachment.id === id,
    );

    let content = editor?.getJSON();
    if (deletedAttachment) content = removeMention(id);

    mutate({ chatbotId, attachments: filteredAttachments, input: content });
  };

  const saveChat = useCallback(
    (input: JSONContent, mentions: AnaraAttachment[], chatbotId: string) => {
      if (isPublicPaper) return;

      const currentAttachments = convertAttachments(
        utils.chatbotInput.getNewChatInput.getData({
          chatbotId,
        })?.attachments ?? [],
      );

      // Filter out existing mentions
      const nonMentionAttachments = currentAttachments.filter(
        (attachment) => attachment.data.type !== 'mention',
      );

      // Combine non-mention attachments with new mentions
      const updatedAttachments = [...nonMentionAttachments, ...mentions];

      mutate({
        chatbotId,
        attachments: updatedAttachments,
        input,
      });

      return updatedAttachments;
    },
    [],
  );

  //TODO, this is probably not the right way to do this
  const debouncedSaveChat = useDebouncedCallback(saveChat, 500);

  const resetAttachments = (chatbotId?: string) => {
    if (isPublicPaper) {
      cleanupEditor();

      return;
    }

    if (!chatbotId) return;
    mutate({ chatbotId, attachments: [], input: '' });
    cleanupEditor();
    setTimeout(() => {
      mutate({ chatbotId, attachments: [], input: '' });
      cleanupEditor();
    }, 550);

    debouncedSaveChat.cancel();
  };

  return {
    addAttachments,
    removeAttachment,
    saveChat: debouncedSaveChat,
    nonDebouncedSaveChat: saveChat,
    resetAttachments,
  };
};
